import { UserGroupClient } from '@frontend/group/api';
import { PackageType } from '@frontend/package/types';
import { SettingKey } from '@frontend/setting/types';
import { TransactionUserClient } from '@frontend/transaction/api';
import { TransactionUser, TransactionUserType } from '@frontend/transaction/types';
import { UserClient } from '@frontend/user/api';
import { IUser } from '@frontend/user/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { syncExistingTransactionPackagesProof } from '../../utils/ensure-transaction-collis-and-packages';
import { syncTransactionUsers } from '../../utils/sync-wizard-transaction-data';
import { useScreen } from '../useScreen';

interface PendingPackage {
    type: PackageType;
    require_proof_of_identity?: boolean;
}

interface ViewProps {
    isLoading: boolean;
    senders: IUser[] | null;
    receivers: IUser[] | null;
    selectedSenders: (string | null)[];
    selectedReceivers: (string | null)[];
    senderSubsets: (string[] | undefined)[];
    receiverSubsets: (string[] | undefined)[];
    addSender: () => void;
    addReceiver: () => void;
    removeSender: (index: number) => void;
    removeReceiver: (index: number) => void;
    updateSender: (index: number, senderId: string | null) => void;
    updateReceiver: (index: number, receiverId: string | null) => void;
    requireProofOfIdentity: boolean;
    dropOffRequireProofOfIdentity: boolean;
    pickUpRequireProofOfIdentity: boolean;
    setDropOffRequireProofOfIdentity: (value: boolean) => void;
    setPickUpRequireProofOfIdentity: (value: boolean) => void;
    submitDisabled: boolean;
    onSubmit: () => void;
    onBack: (type: ScreenType) => void;
}

const updatePackageProofRequirement = (packages: PendingPackage[], packageType: PackageType, value: boolean): PendingPackage[] =>
    packages.map((pkg) =>
        pkg.type === packageType
            ? {
                  ...pkg,
                  require_proof_of_identity: value
              }
            : pkg
    );

const useSelectSendersAndReceivers = (props: { wizard_id: string; requireProofOfIdentity?: boolean }): ViewProps => {
    const { store, onBack, updateWizardData, onSubmit: navigateToNextStep } = useScreen(props.wizard_id);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [senders, setSenders] = useState<IUser[] | null>(null);
    const [receivers, setReceivers] = useState<IUser[] | null>(null);
    const [dropOffRequireProofOfIdentity, setDropOffRequireProofOfIdentityState] = useState<boolean>(store?.data.dropOffRequireProofOfIdentity ?? false);
    const [pickUpRequireProofOfIdentity, setPickUpRequireProofOfIdentityState] = useState<boolean>(store?.data.pickUpRequireProofOfIdentity ?? false);
    const [selectedSenders, setSelectedSenders] = useState<(string | null)[]>(() => {
        const senderIds = ((store?.data?.senders as TransactionUser[]) || []).map((sender) => sender.user_id).filter((userId): userId is string => !!userId);
        return senderIds.length > 0 ? senderIds : [null];
    });
    const [selectedReceivers, setSelectedReceivers] = useState<(string | null)[]>(() => {
        const receiverIds = ((store?.data?.receivers as TransactionUser[]) || [])
            .map((receiver) => receiver.user_id)
            .filter((userId): userId is string => !!userId);
        return receiverIds.length > 0 ? receiverIds : [null];
    });
    const selectedSendersRef = useRef(selectedSenders);
    const selectedReceiversRef = useRef(selectedReceivers);
    const dropOffRequireProofRef = useRef(dropOffRequireProofOfIdentity);
    const pickUpRequireProofRef = useRef(pickUpRequireProofOfIdentity);
    const requireProofOfIdentity = props.requireProofOfIdentity ?? false;
    const screenType = requireProofOfIdentity ? ScreenType.SELECT_SENDERS_AND_RECEIVERS_WITH_PROOF_OF_IDENTITY : ScreenType.SELECT_SENDERS_AND_RECEIVERS;

    useEffect(() => {
        selectedSendersRef.current = selectedSenders;
    }, [selectedSenders]);

    useEffect(() => {
        selectedReceiversRef.current = selectedReceivers;
    }, [selectedReceivers]);

    useEffect(() => {
        dropOffRequireProofRef.current = dropOffRequireProofOfIdentity;
    }, [dropOffRequireProofOfIdentity]);

    useEffect(() => {
        pickUpRequireProofRef.current = pickUpRequireProofOfIdentity;
    }, [pickUpRequireProofOfIdentity]);

    const getProofOfIdentityWizardData = (dropProof: boolean, pickProof: boolean) => {
        const pendingPackages = store?.data.pendingPackages as PendingPackage[] | undefined;
        const updatedDropPackages = pendingPackages ? updatePackageProofRequirement(pendingPackages, PackageType.DROP_OFF, dropProof) : undefined;
        const updatedPackages = updatedDropPackages ? updatePackageProofRequirement(updatedDropPackages, PackageType.PICK_UP, pickProof) : undefined;

        return {
            dropOffRequireProofOfIdentity: dropProof,
            pickUpRequireProofOfIdentity: pickProof,
            ...(updatedPackages ? { pendingPackages: updatedPackages } : {})
        };
    };

    const setDropOffRequireProofOfIdentity = (value: boolean) => {
        setDropOffRequireProofOfIdentityState(value);
        updateWizardData(getProofOfIdentityWizardData(value, pickUpRequireProofRef.current));
    };

    const setPickUpRequireProofOfIdentity = (value: boolean) => {
        setPickUpRequireProofOfIdentityState(value);
        updateWizardData(getProofOfIdentityWizardData(dropOffRequireProofRef.current, value));
    };

    useEffect(() => {
        const senderUserGroupId = store?.functionalitySettings?.find((fs) => fs.key === SettingKey.OPEN_DROP_OFF_SENDERS_USER_GROUP_ID)?.value;
        const receiverUserGroupId = store?.functionalitySettings?.find((fs) => fs.key === SettingKey.OPEN_DROP_OFF_RECEIVERS_USER_GROUP_ID)?.value;

        const loadUsers = async () => {
            try {
                const [senderMembers, receiverMembers] = await Promise.all([
                    senderUserGroupId && typeof senderUserGroupId === 'string'
                        ? UserGroupClient.fetchUserGroupMembers({ group_id: senderUserGroupId })
                        : Promise.resolve(null),
                    receiverUserGroupId && typeof receiverUserGroupId === 'string'
                        ? UserGroupClient.fetchUserGroupMembers({ group_id: receiverUserGroupId })
                        : Promise.resolve(null)
                ]);

                const [senderResponse, receiverResponse] = await Promise.all([
                    senderMembers ? UserClient.resolveUsers(senderMembers.results.map((member) => member.entity_id)) : Promise.resolve(null),
                    receiverMembers ? UserClient.resolveUsers(receiverMembers.results.map((member) => member.entity_id)) : Promise.resolve(null)
                ]);

                setSenders(senderResponse?.results ?? null);
                setReceivers(receiverResponse?.results ?? null);

                if (store?.data.transaction?.id) {
                    const [fetchedSenders, fetchedReceivers] = await Promise.all([
                        TransactionUserClient.fetchTransactionUsers({
                            transaction_id: store.data.transaction.id,
                            type: TransactionUserType.SENDER
                        }),
                        TransactionUserClient.fetchTransactionUsers({
                            transaction_id: store.data.transaction.id,
                            type: TransactionUserType.RECEIVER
                        })
                    ]);

                    updateWizardData({
                        ...(fetchedSenders.results.length > 0 ? { senders: fetchedSenders.results } : {}),
                        ...(fetchedReceivers.results.length > 0 ? { receivers: fetchedReceivers.results } : {})
                    });

                    if (fetchedSenders.results.length > 0) {
                        setSelectedSenders(fetchedSenders.results.map((sender) => sender.user_id));
                    }

                    if (fetchedReceivers.results.length > 0) {
                        setSelectedReceivers(fetchedReceivers.results.map((receiver) => receiver.user_id));
                    }
                }
            } finally {
                setIsLoading(false);
            }
        };

        loadUsers();
    }, []);

    const addSender = () => {
        setSelectedSenders([...selectedSendersRef.current, null]);
    };

    const addReceiver = () => {
        setSelectedReceivers([...selectedReceiversRef.current, null]);
    };

    const removeSender = (index: number) => {
        setSelectedSenders(selectedSendersRef.current.filter((_, i) => i !== index));
    };

    const removeReceiver = (index: number) => {
        setSelectedReceivers(selectedReceiversRef.current.filter((_, i) => i !== index));
    };

    const updateSender = useCallback((index: number, senderId: string | null) => {
        const updated = [...selectedSendersRef.current];
        updated[index] = senderId;
        setSelectedSenders(updated);
    }, []);

    const updateReceiver = useCallback((index: number, receiverId: string | null) => {
        const updated = [...selectedReceiversRef.current];
        updated[index] = receiverId;
        setSelectedReceivers(updated);
    }, []);

    const senderSubsets = useMemo(
        () =>
            selectedSenders.map((senderId) =>
                senders
                    ?.filter((sender) => {
                        const found = selectedSenders.find((id) => id === sender.id);
                        return found ? senderId === found : true;
                    })
                    .map((sender) => sender.id)
            ),
        [senders, selectedSenders]
    );

    const receiverSubsets = useMemo(
        () =>
            selectedReceivers.map((receiverId) =>
                receivers
                    ?.filter((receiver) => {
                        const found = selectedReceivers.find((id) => id === receiver.id);
                        return found ? receiverId === found : true;
                    })
                    .map((receiver) => receiver.id)
            ),
        [receivers, selectedReceivers]
    );

    const canContinue = selectedSenders.some((sender) => sender !== null) && selectedReceivers.some((receiver) => receiver !== null);

    const onSubmit = async () => {
        if (!store || !canContinue || !store.data.transaction?.id) return;

        setIsLoading(true);
        try {
            const accountId = store.wizard.account_id;
            const transactionId = store.data.transaction.id;
            const relevantSenders = selectedSendersRef.current.filter((sender): sender is string => sender !== null);
            const relevantReceivers = selectedReceiversRef.current.filter((receiver): receiver is string => receiver !== null);

            const [syncedSenders, syncedReceivers] = await Promise.all([
                syncTransactionUsers(accountId, transactionId, TransactionUserType.SENDER, relevantSenders),
                syncTransactionUsers(accountId, transactionId, TransactionUserType.RECEIVER, relevantReceivers)
            ]);

            const proofData = requireProofOfIdentity ? getProofOfIdentityWizardData(dropOffRequireProofRef.current, pickUpRequireProofRef.current) : undefined;

            const packages = requireProofOfIdentity
                ? await syncExistingTransactionPackagesProof(store, store.data.transaction, {
                      drop: dropOffRequireProofRef.current,
                      pick: pickUpRequireProofRef.current
                  })
                : undefined;

            updateWizardData({
                senders: syncedSenders,
                receivers: syncedReceivers,
                ...(proofData ?? {}),
                ...(packages ? { packages } : {})
            });

            const nextStep = store.steps.find((step) => step.screen === screenType && step.trigger === ScreenTrigger.SUBMIT);
            navigateToNextStep(nextStep);
        } finally {
            setIsLoading(false);
        }
    };

    return {
        isLoading,
        senders,
        receivers,
        selectedSenders,
        selectedReceivers,
        senderSubsets,
        receiverSubsets,
        addSender,
        addReceiver,
        removeSender,
        removeReceiver,
        updateSender,
        updateReceiver,
        requireProofOfIdentity,
        dropOffRequireProofOfIdentity,
        pickUpRequireProofOfIdentity,
        setDropOffRequireProofOfIdentity,
        setPickUpRequireProofOfIdentity,
        submitDisabled: !canContinue || !store?.data.transaction || isLoading,
        onSubmit,
        onBack
    };
};

export default useSelectSendersAndReceivers;
